/**
 * Markdown documents as a first-class archspace surface (§4.15).
 *
 * A `.md` file is a real node in the model: it carries flavored-markdown
 * (the same `[[ … ]]` / `@` grammar as descriptions, via `scanRefs`) and
 * participates in cross-references + backlinks. A `.md` never declares
 * canonical model elements — its body is verbatim prose; only its
 * section headings and its references are structured.
 */

import type { QualifiedName } from "./../types.js";

export interface SectionHeading {
  /** Heading text, trimmed (without the leading `#`s). */
  readonly text: string;
  /** Slugified, collision-suffixed anchor (`[[Doc#slug]]` targets this). */
  readonly slug: string;
  /** Heading depth 1–6. */
  readonly level: number;
  /** Byte offset of the heading line in the raw content. */
  readonly offset: number;
}

export interface MarkdownDocument {
  /** Display name — frontmatter `title:` if set, else the file stem. */
  readonly name: string;
  /** Source path (e.g. `/docs/runbook.md`). */
  readonly path: string;
  /** Owning package — frontmatter `package:`, else undefined (repo root). */
  readonly packageName?: string;
  /** Owning space — frontmatter `space:` (dotted), else derived from the path. */
  readonly space?: QualifiedName;
  /** Parsed frontmatter key→value (simple `key: value` lines only). */
  readonly frontmatter: Readonly<Record<string, string>>;
  /** Raw markdown body, frontmatter stripped, verbatim. */
  readonly body: string;
  /** Section headings in document order, slugs disambiguated. */
  readonly sections: readonly SectionHeading[];
}

/** Strip a `.md`/`.markdown` extension and any directory prefix. */
function fileStem(path: string): string {
  const base = path.slice(path.lastIndexOf("/") + 1);
  return base.replace(/\.(md|markdown)$/i, "");
}

/** GitHub-style slug: lowercase, spaces→`-`, drop non `[a-z0-9-]`. */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Parse a `.md` file into a `MarkdownDocument`. Frontmatter is an optional
 * leading `---` … `---` block of `key: value` lines. The body is kept
 * verbatim (offsets are relative to the raw `content`, so diagnostics point
 * at the real source).
 */
export function parseMarkdownDocument(path: string, content: string): MarkdownDocument {
  let frontmatter: Record<string, string> = {};
  let bodyStart = 0;

  // Optional YAML-ish frontmatter: a leading `---` line, key:value lines,
  // a closing `---` line. Only flat `key: value` is recognized.
  if (content.startsWith("---\n") || content.startsWith("---\r\n")) {
    const openEnd = content.indexOf("\n"); // end of the opening `---` line
    // The close is the next line that is *exactly* `---` (a `--- note` or a
    // `----` horizontal rule in the body is not a fence close).
    let scan = openEnd + 1;
    let closeStart = -1;
    while (scan <= content.length) {
      const nl = content.indexOf("\n", scan);
      const lineEnd = nl === -1 ? content.length : nl;
      if (content.slice(scan, lineEnd).trim() === "---") {
        closeStart = scan;
        bodyStart = nl === -1 ? content.length : nl + 1;
        break;
      }
      if (nl === -1) break;
      scan = nl + 1;
    }
    if (closeStart !== -1) {
      frontmatter = parseFrontmatter(content.slice(openEnd + 1, closeStart));
    }
  }

  const body = content.slice(bodyStart);
  const name = frontmatter.title?.trim() || fileStem(path);
  const packageName = frontmatter.package?.trim() || undefined;
  // A PRESENT `space:` key is authoritative — even when empty. An empty (or
  // root-sentinel) value pins the document to the ROOT space, distinct from an
  // ABSENT key which derives the space from the directory path. Testing
  // `frontmatter.space` (truthiness) would misread empty as absent and fall
  // through to the bogus path-derived space (e.g. `docs/decisions/…`).
  const space = "space" in frontmatter
    ? (frontmatter.space.trim()
        ? (frontmatter.space.trim().split(".").filter(Boolean) as QualifiedName)
        : undefined)
    : spaceFromPath(path);

  return {
    name,
    path,
    packageName,
    space,
    frontmatter,
    body,
    sections: extractSections(content, bodyStart),
  };
}

function parseFrontmatter(block: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of block.split("\n")) {
    const colon = line.indexOf(":");
    if (colon === -1) continue;
    const key = line.slice(0, colon).trim();
    if (!key || /\s/.test(key)) continue;
    let value = line.slice(colon + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    out[key] = value;
  }
  return out;
}

/** Derive a space from the directory path (e.g. `/billing/notes.md` → `["billing"]`). */
function spaceFromPath(path: string): QualifiedName | undefined {
  const dir = path.slice(0, path.lastIndexOf("/"));
  const segs = dir.split("/").filter((s) => s && s !== ".");
  return segs.length > 0 ? (segs as QualifiedName) : undefined;
}

/** Headings — both ATX (`# …`) and setext (a text line underlined by `===` →
 *  level 1 or `---` → level 2). Slugs are disambiguated by a numeric suffix. A
 *  `#` line inside a fenced code block (``` or ~~~) is code, not a heading. */
function extractSections(content: string, bodyStart: number): SectionHeading[] {
  const sections: SectionHeading[] = [];
  const seen = new Map<string, number>();
  const headingRe = /^(#{1,6})\s+(.+?)\s*#*\s*$/;
  const setextRe = /^(=+|-+)\s*$/; // the underline line
  const fenceRe = /^\s*(`{3,}|~{3,})/;
  let fenceMarker: "`" | "~" | null = null;

  const add = (text: string, level: number, offset: number): void => {
    const base = slugify(text);
    let count = seen.get(base) ?? 0;
    let slug = count > 0 ? `${base}-${count}` : base;
    // The suffixed slug could itself collide with an explicit heading
    // (e.g. `A`, `A`, `A-1`); bump until the slug is actually free.
    while (seen.has(slug)) slug = `${base}-${++count}`;
    seen.set(base, count + 1);
    seen.set(slug, 1);
    sections.push({ text, slug, level, offset });
  };

  const lines = content.slice(bodyStart).split("\n");
  let offset = bodyStart;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const fence = fenceRe.exec(line);
    if (fence) {
      const marker = fence[1]![0] as "`" | "~";
      if (fenceMarker === null) fenceMarker = marker;
      else if (fenceMarker === marker) fenceMarker = null; // only a matching marker closes
    } else if (fenceMarker === null) {
      const atx = headingRe.exec(line);
      if (atx) {
        add(atx[2]!.trim(), atx[1]!.length, offset);
      } else {
        // Setext: a non-blank paragraph line whose NEXT line is all `=`/`-`. The
        // title must be plain text (not a list / quote / table / blank / its own
        // underline) so a body `---` horizontal rule isn't read as a heading.
        const next = lines[i + 1];
        const title = line.trim();
        if (
          next !== undefined &&
          setextRe.test(next) &&
          title.length > 0 &&
          !/^[-*+]\s|^>|^\|/.test(title) &&
          !setextRe.test(title)
        ) {
          add(title, next[0] === "=" ? 1 : 2, offset);
        }
      }
    }
    offset += line.length + 1; // + the consumed "\n"
  }
  return sections;
}
